/**
 * The button that opens the AI console, and the console it opens.
 *
 * Self-contained on purpose: the caller passes who the patient is and drops
 * this in. The Live View already carries a lot of state, and threading modal
 * open/closed through it for one feature would spread this across two files
 * for no benefit.
 *
 * On prominence — the brief was that the AI must not look like a secondary
 * tool. It sits full-width above the three existing consultation actions
 * rather than beside them as a fourth: those three are equals (record,
 * prescription, done) and a fourth peer would read as one more of the same.
 * A full-width bar above them reads as the thing you do first.
 */

import { useState } from 'react';
import { Icon } from '../../components/common';
import { useAiCapabilities } from './useAiAnalysis';
import { MedicalAiAnalyzer } from './MedicalAiAnalyzer';

/**
 * @param {object} props
 * @param {number|null} props.patientId          Registered patient's user id.
 * @param {string|null} props.manualPatientCode  Walk-in patient's code instead.
 * @param {string}      props.patientName
 * @param {string}     [props.specialty]         Weights the analysis, does not limit it.
 * @param {string}     [props.className]
 */
export function AiAnalyzeButton({
  patientId,
  manualPatientCode,
  patientName,
  specialty,
  className = '',
}) {
  const [open, setOpen] = useState(false);

  // Fetched once per session and cached in the hook's module scope, so this
  // costs nothing on the re-render storm the live queue's 2s poll produces.
  const { canUseAi, engineConfigured, loading } = useAiCapabilities();

  // Nothing to analyse — a booking with neither a patient id nor a code cannot
  // be resolved to a file, and the server would refuse it anyway.
  if (!patientId && !manualPatientCode) return null;

  // Explicitly revoked for this account. Hidden rather than disabled: a doctor
  // whose AI access was switched off should not be shown a permanently dead
  // control on every consultation.
  if (!loading && !canUseAi) return null;

  const disabled = !engineConfigured;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        title={
          disabled
            ? 'محرك التحليل الذكي غير مُهيّأ على الخادم'
            : 'تحليل الملف الطبي كاملاً واكتشاف ما هو غير طبيعي'
        }
        className={`ai-trigger group flex w-full items-center gap-3 rounded-2xl px-4 py-3.5 text-white ${className}`}
      >
        <span className="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm">
          <Icon
            name="sparkles"
            size={19}
            className="transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12"
          />
        </span>

        <span className="relative z-10 min-w-0 flex-1 text-right">
          <span className="block text-sm font-black leading-tight">
            حلّل الملف الطبي بالذكاء الاصطناعي
          </span>
          <span className="mt-0.5 block truncate text-[11px] font-medium text-white/70">
            {disabled
              ? 'غير متاح حالياً — راجع إعدادات الخادم'
              : 'اكتشاف ما هو غير طبيعي في الملف كاملاً — تقرير في 10 أسطر'}
          </span>
        </span>

        <span className="relative z-10 flex shrink-0 items-center gap-1 rounded-lg bg-black/25 px-2 py-1 text-[10px] font-black tracking-wider">
          AI
          <Icon
            name="chevron-left"
            size={12}
            className="transition-transform group-hover:-translate-x-0.5"
          />
        </span>
      </button>

      <MedicalAiAnalyzer
        open={open}
        onClose={() => setOpen(false)}
        patientId={patientId}
        manualPatientCode={manualPatientCode}
        patientName={patientName}
        specialty={specialty}
      />
    </>
  );
}

export default AiAnalyzeButton;
